import type { HydratedAsset } from '../publicDto';

// Shared image / video renderer used by both StoryMockup and FeedMockup.
//
// Image:    <img> object-cover fills the container, native lazy-load.
// Video:    <iframe> pointed at Drive's /file/d/{id}/preview embed —
//           same pattern the AM-facing lightbox uses. Plays without
//           the franchisee being authenticated to Google AS LONG AS
//           the Drive file is shared with "Anyone with the link" (or
//           the franchisee's specific account). Spec §13.5 documents
//           the operational requirement.
// Other:    "preview not available" placeholder. Drive doesn't return
//           thumbnails for PDFs / docs in this surface; we don't want
//           to render those as mockup creative anyway.

type Props = {
  asset: HydratedAsset;
  // Tailwind class that gives the container its aspect ratio
  // ("aspect-[9/16]" for Story, "aspect-[4/5]" for Feed). Caller sets
  // the width via parent layout.
  aspectClass: string;
};

const IMAGE_PREFIX = 'image/';
const VIDEO_PREFIX = 'video/';

export function AssetMedia({ asset, aspectClass }: Props) {
  const isImage = asset.mimeType.startsWith(IMAGE_PREFIX);
  const isVideo = asset.mimeType.startsWith(VIDEO_PREFIX);

  if (isImage) {
    return (
      <div className={`${aspectClass} w-full overflow-hidden bg-slate-900`}>
        <img
          src={asset.thumbnailUrl ?? asset.webContentLink ?? ''}
          alt={asset.label || asset.driveFileName}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      </div>
    );
  }
  if (isVideo) {
    return <VideoAsset asset={asset} aspectClass={aspectClass} />;
  }
  return (
    <div
      className={`${aspectClass} w-full bg-slate-100 flex items-center justify-center`}
    >
      <p className="text-xs text-slate-400">Preview not available</p>
    </div>
  );
}

function VideoAsset({ asset, aspectClass }: Props) {
  // Stage 1 §13.5. Switched from <video src=webContentLink> to a
  // Drive `/file/d/{id}/preview` iframe. Reason: webContentLink
  // requires an authed redirect (Google session), which franchisees
  // don't have. The lightbox already settled on the iframe pattern
  // for the same reason; using it here keeps playback authoring
  // consistent.
  //
  // The iframe lazy-loads (loading="lazy") so off-tab video URLs
  // don't fetch the embed shell until the tab becomes visible. The
  // existing tab-mount-gating in PreviewPage already prevents the
  // iframe from mounting at all when the tab is inactive.
  //
  // No autoplay-on-scroll wiring here — the Drive embed handles its
  // own playback controls. Pages with many video assets won't
  // autoplay all at once because the embed only starts on user click.
  return (
    <div
      className={`${aspectClass} w-full overflow-hidden bg-black`}
    >
      <iframe
        title={`Preview: ${asset.label || asset.driveFileName}`}
        src={asset.previewEmbedUrl}
        className="w-full h-full"
        // allowfullscreen lets the embed open the Drive player full-
        // screen on mobile / desktop. autoplay isn't requested — see
        // the rationale above.
        allow="fullscreen"
        allowFullScreen
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
    </div>
  );
}
